import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Dialog, IconButton, SwipeableDrawer, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import sevakMandala from "../../../assets/Images/Temples/sevak-mandala.svg";
import sevakMandalaMobile from "../../../assets/Images/Temples/m-sevak-mandala.svg";
import { HINDI_FONT, OPEN_MONTHLY_BOX_EVENT, TEMPLE_COLORS, TempleButton } from "../shared";
import { spin } from "../TempleHero/motion";
import TempleDonationBox from "../../../components/common/TempleDonationBox";
import useCheckoutReminder from "../../../components/common/CheckoutReminder/useCheckoutReminder";
import templesHeroPhoto from "../../../assets/Images/Temples/hero-photo.png";
import { getTempleSubCategories } from "../../../api/services/templesService";

// iOS needs different SwipeableDrawer transition handling for a smooth gesture (as on Life).
const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

// Turns like the hero mandalas, at half their speed.
const slowSpin = {
  animation: `${spin} 180s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const MonthlySevakSection = ({ content, donationBox }) => {
  const isMobile = useMediaQuery("(max-width: 599px)");

  // "Start Monthly Giving" opens the temple monthly donation box: a bottom sheet on
  // mobile (like Life's Donate Now), a modal on desktop. The dropdown's
  // options (Temple's sub-categories) load the first time it opens.
  const [boxOpen, setBoxOpen] = useState(false);
  const [subCategories, setSubCategories] = useState(null);

  const subCategoriesRequested = useRef(false);

  // Closed the Razorpay checkout without paying → stay on /temples; Back shows the reminder popup.
  // No campaign here, so the popup's card shows this section's title and the page's hero photo.
  const reminder = useCheckoutReminder({ title: `${content.titleLine1} ${content.titleLine2}`.trim() || "Temple seva", image: templesHeroPhoto });

  const openBox = () => {
    setBoxOpen(true);
    if (!subCategoriesRequested.current) {
      subCategoriesRequested.current = true;
      getTempleSubCategories()
        .then(setSubCategories)
        .catch(() => setSubCategories([]));
    }
  };

  // Other buttons on the page (e.g. the hero's "Donate Monthly") open this box too.
  useEffect(() => {
    window.addEventListener(OPEN_MONTHLY_BOX_EVENT, openBox);
    return () => window.removeEventListener(OPEN_MONTHLY_BOX_EVENT, openBox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const boxContent = (
    <Box sx={{ position: "relative" }}>
      {/* Drag handle on the red header — swipe it (or the sheet) down to dismiss */}
      {isMobile && (
        <Box sx={{ position: "absolute", top: 8, left: 0, right: 0, zIndex: 2, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <Box sx={{ width: "40px", height: "4px", background: "rgba(255, 255, 255, 0.55)", borderRadius: "2px" }} />
        </Box>
      )}
      <IconButton
        aria-label="Close"
        onClick={() => setBoxOpen(false)}
        // Sits on the box's red header (every step of the box has it).
        sx={{ position: "absolute", top: 10, right: 10, zIndex: 2, color: "#FFFFFF", background: "rgba(255, 255, 255, 0.14)", "&:hover": { background: "rgba(255, 255, 255, 0.26)" }, "&:focus-visible": { outline: "2px solid #F3D27A" } }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {subCategories === null ? (
        <Box sx={{ background: "#FFFFFF", borderRadius: "18px", py: 8, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: TEMPLE_COLORS.red }} />
        </Box>
      ) : (
        <TempleDonationBox
          subCategories={subCategories}
          config={donationBox}
          campaignTitle={`${content.titleLine1} ${content.titleLine2}`.trim()}
          onCheckoutAbandoned={(details) => {
            setBoxOpen(false);
            reminder.onCheckoutAbandoned(details);
          }}
        />
      )}
    </Box>
  );

  return (
    <>
    {reminder.modal}
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(179.57deg, #1D1D1B 0.89%, #2A2A28 99.11%)",
        px: isMobile ? "24px" : "40px",
        py: isMobile ? "76px" : "100px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: isMobile ? "100px" : "60px",
        textAlign: "center",
      }}
    >
      <Box
        component="img"
        alt=""
        src={isMobile ? sevakMandalaMobile : sevakMandala}
        sx={
          isMobile
            ? // Mobile art (its own stroke weight) at design size, horizontally
              // centred, 56.6px from the top as in the mobile design.
              { position: "absolute", left: "calc(50% - 240.7px)", top: 56.6, width: 481.4, height: 481.4, maxWidth: "none", pointerEvents: "none", ...slowSpin }
            : { position: "absolute", left: "calc(50% - 432.7px)", top: 18, width: 865.4, height: 865.4, pointerEvents: "none", ...slowSpin }
        }
      />

      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "40px" }}>
        <Typography sx={{ fontFamily: HINDI_FONT, fontSize: isMobile ? "20px" : "26px", lineHeight: isMobile ? "28px" : "34px", color: TEMPLE_COLORS.red }}>
          {content.tagline}
        </Typography>
        <Typography
          component="h2"
          sx={{ fontWeight: 700, fontSize: isMobile ? "30px" : "56px", lineHeight: isMobile ? "38px" : "66px", color: "#FFFFFF" }}
        >
          {content.titleLine1}
          <Box component="span" sx={{ display: "block" }}>
            {content.titleLine2}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: isMobile ? "15px" : "18px", lineHeight: isMobile ? "24px" : "28px", color: "rgba(255, 255, 255, 0.75)", maxWidth: 836 }}>
          {content.description}
        </Typography>
      </Box>

      <Box sx={{ position: "relative", pt: isMobile ? "6px" : "10px", width: isMobile ? "100%" : "auto" }}>
        <TempleButton
          onClick={openBox}
          sx={isMobile ? { width: "100%", fontSize: "15px", py: "15px", px: "24px", boxShadow: "none" } : undefined}
        >
          {content.cta.label}
        </TempleButton>
      </Box>

      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={boxOpen}
          onClose={() => setBoxOpen(false)}
          onOpen={() => setBoxOpen(true)}
          disableSwipeToOpen
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
              maxHeight: "92vh",
              overflow: "auto",
              background: "#FFFFFF",
            },
          }}
        >
          {boxContent}
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={boxOpen}
          onClose={() => setBoxOpen(false)}
          fullWidth
          maxWidth={false}
          PaperProps={{ sx: { width: 534, m: "32px", background: "transparent", boxShadow: "none", overflow: "visible" } }}
        >
          {boxContent}
        </Dialog>
      )}
    </Box>
    </>
  );
};

export default MonthlySevakSection;
